import { daysUntil } from './dates';

export const STATUSES = [
  { value: 'todo', label: 'To do' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'needs_review', label: 'Needs review' },
  { value: 'complete', label: 'Complete' },
];
export const statusLabel = (value) => (STATUSES.find((s) => s.value === value) || {}).label || value;

export const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
];
const PRIORITY_RANK = { high: 0, normal: 1, low: 2 };

export const SORT_OPTIONS = [
  { value: 'due', label: 'Due date' },
  { value: 'completed', label: 'Recently completed', completeOnly: true },
  { value: 'manual', label: 'Manual order' },
  { value: 'priority', label: 'Priority' },
  { value: 'added', label: 'Recently added' },
  { value: 'updated', label: 'Recently updated' },
  { value: 'title', label: 'Title A–Z' },
  { value: 'assignee', label: 'Assignee' },
  { value: 'progress', label: 'Progress' },
];

export const progressOf = (task) => {
  const steps = task.checklist || [];
  if (steps.length === 0) return null;
  return { done: steps.filter((s) => s.done).length, total: steps.length };
};

export const matchesTab = (task, tab) => {
  if (tab === 'all') return true;
  if (tab === 'complete') return task.status === 'complete';
  return task.status !== 'complete';
};

export const matchesSearch = (task, query) => {
  const q = (query || '').trim().toLowerCase();
  if (!q) return true;
  return [task.title, task.description, task.assignee_name].some((v) => (v || '').toLowerCase().includes(q));
};

export const filterTasks = (tasks, tab, query) => tasks.filter((t) => matchesTab(t, tab) && matchesSearch(t, query));

const byManual = (a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id - b.id;
const byTime = (key, dir) => (a, b) => dir * ((a[key] ? Date.parse(a[key]) : 0) - (b[key] ? Date.parse(b[key]) : 0));

// Sort a filtered list. `today` only matters for 'due', which puts undated tasks last.
export const sortTasks = (tasks, sort, today) => {
  const list = [...tasks];
  const compare = {
    due: (a, b) => {
      const da = daysUntil(a.due_on, today);
      const db = daysUntil(b.due_on, today);
      if (da === null && db === null) return byManual(a, b);
      if (da === null) return 1;
      if (db === null) return -1;
      return da - db || byManual(a, b);
    },
    completed: (a, b) => byTime('completed_at', -1)(a, b) || byManual(a, b),
    manual: byManual,
    priority: (a, b) => PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] || byManual(a, b),
    added: (a, b) => byTime('created_at', -1)(a, b) || byManual(a, b),
    updated: (a, b) => byTime('updated_at', -1)(a, b) || byManual(a, b),
    title: (a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }) || byManual(a, b),
    assignee: (a, b) => {
      // Unassigned last.
      if (!a.assignee_name && !b.assignee_name) return byManual(a, b);
      if (!a.assignee_name) return 1;
      if (!b.assignee_name) return -1;
      return a.assignee_name.localeCompare(b.assignee_name) || byManual(a, b);
    },
    progress: (a, b) => {
      // Furthest along first; tasks without a checklist last.
      const pa = progressOf(a);
      const pb = progressOf(b);
      if (!pa && !pb) return byManual(a, b);
      if (!pa) return 1;
      if (!pb) return -1;
      return (pb.done / pb.total) - (pa.done / pa.total) || byManual(a, b);
    },
  }[sort] || byManual;
  return list.sort(compare);
};

// Move an item within a list; returns a new list.
export const moveItem = (list, from, to) => {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};
